import { find, get, set } from 'lodash';
import React, { Component, ReactNode } from 'react';
import type { LocationDescriptorObject } from 'history';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import type { CardFlowEvent, StoryStep } from 'types/story';
import { CARD_TYPE, CONNECTION_TYPES, FLOW_DEFAULT_EVENT_NAME, PARAMS } from 'common/constants';
import { parseURL, searchObjectToString } from 'common/utils/parse-url';
import { clientLog } from 'common/utils/helpers';
import { location } from 'common/utils/url-helper';
import { StoryFacade } from 'common/utils/facades/story-facade';
import { IFRAME_ACTIONS, transmitTo } from 'common/utils/iframe-tunnel';
import { CountType, NavigationFacade } from 'common/utils/facades/navigation-card-facade';
import { CARD_SHARE_PATH } from 'client/constants/routes';
import { ComponentEvents, EventProviderContext, EventProviderContextT } from './Context';

const log = clientLog.extend('EventProvider');
const { Provider } = EventProviderContext;
const { isPreview, isPreviewRoute, isEmbedDevPreview } = location.client;

const getNextCardSearchObject = ({ nextCardId }: { nextCardId: string }) => {
	if (!nextCardId) {
		return null;
	}

	const { searchObject } = parseURL(window.location.href);

	set(searchObject, PARAMS.CARD_ID, nextCardId);

	return searchObject;
};

const getStepRandomCardId = (params: { steps: StoryStep[]; event: CardFlowEvent }) => {
	const step = find(params.steps, ['_id', params.event.connection!.pointId]);
	return step?.cards[Math.round(Math.random() * (step.cards.length - 1))]?._id;
};

const isConnectedToStep = (event: CardFlowEvent) => event?.connection?.type === CONNECTION_TYPES.STEP;

type Props = RouteComponentProps & {
	story: StoryFacade;
	isEditor: boolean;
	nextDefaultCardId: string;
	events: CardFlowEvent[];
	children: ReactNode;
};

const getPointIdOf = (event: CardFlowEvent): string | undefined => get(event, ['connection', 'pointId']);

class EventProvider extends Component<Props> {
	componentEvents: ComponentEvents = {};

	get events(): EventProviderContextT['eventProvider']['events'] {
		return {
			flow: this.callFlowEvent,
			component: this.componentEvents,
		};
	}

	setComponentEvents: EventProviderContextT['eventProvider']['setComponentEvents'] = (componentId, value) => {
		this.componentEvents[componentId] = {
			...(this.componentEvents[componentId] ?? {}),
			...value,
		};
	};

	callFlowEvent: EventProviderContextT['eventProvider']['events']['flow'] = (eventId, options = {}) => {
		const { events, nextDefaultCardId, history, isEditor, story: storyFacade } = this.props;
		const event = options.isDirectId
			? undefined // btnLink property (link to card from editor settings)
			: find(events, ['componentId', eventId]) || find(events, ['name', FLOW_DEFAULT_EVENT_NAME]);

		// if no event or event isn't connected — navigate to next card
		let nextCardId;
		if (options.isDirectId) nextCardId = eventId;
		else nextCardId = event ? getPointIdOf(event) || nextDefaultCardId : nextDefaultCardId;

		// Get random card id in step
		if (event && isConnectedToStep(event)) {
			const randomCardId = getStepRandomCardId({ steps: storyFacade.steps, event });
			if (randomCardId) nextCardId = randomCardId;
		}

		if (!nextCardId) {
			return; // end
		}

		let { card: nextCard, step: nextStep } = storyFacade.getDataByCardId(nextCardId);

		if (!nextCard) {
			// move to the next default card, in case of direct link is to card that does not exist
			nextCardId = nextDefaultCardId || '404';
			({ card: nextCard, step: nextStep } = storyFacade.getDataByCardId(nextCardId));
		}

		if (nextCard && nextCard.type === CARD_TYPE.NAVIGATION && nextStep) {
			const { storyScore } = storyFacade;
			const navigationFacade = new NavigationFacade(nextCard);
			const countType = navigationFacade.getCountType(storyFacade.settings);
			let nextEvent: CardFlowEvent | null | undefined = null;

			switch (countType) {
				case CountType.score: {
					nextEvent = navigationFacade.nextEvent[countType](storyScore[countType]());
					break;
				}
				case CountType.character: {
					nextEvent = navigationFacade.nextEvent[countType](storyScore[countType]());
					break;
				}
				default:
					break;
			}

			let redirectTo = nextEvent?.connection?.pointId; // next card id

			// Redirect to random card id in step in case of connected to step
			if (nextEvent && isConnectedToStep(nextEvent)) {
				const randomCardId = getStepRandomCardId({ steps: storyFacade.steps, event: nextEvent });
				if (randomCardId) redirectTo = randomCardId;
			}

			// Redirect to 1st card in next step in case of default event or connection does not exists
			if (!redirectTo) {
				redirectTo = this.props.story.getNextDefaultCard(nextStep)?._id;
			}

			if (redirectTo) {
				log('Navigation: redirect to', redirectTo);
				nextCardId = redirectTo;
			} else {
				log('Navigation: redirect is not possible');
				return; // end
			}
		}

		const nextCardSearchObject = getNextCardSearchObject({ nextCardId })!;
		const search = nextCardSearchObject ? searchObjectToString(nextCardSearchObject) : '';

		const nextLocation: LocationDescriptorObject = {
			pathname: isPreview ? window.location.pathname : CARD_SHARE_PATH.replace(':cardId', nextCardId),
			search,
			// Handles hash-based scrolling via the 'ScrollToElement' component on hash change
			hash: options.scrollTo,
		};

		log('go to', nextLocation);

		if (options.reload) {
			window.location.href = `${window.location.origin}${nextLocation.pathname}${nextLocation.search}`;
		} else {
			history.push(nextLocation);
		}

		if (!isEditor && !isEmbedDevPreview && isPreviewRoute) {
			transmitTo({
				id: 'EventProvider',
				target: 'admin',
				action: IFRAME_ACTIONS.UPDATE_ADMIN_PREVIEW_LOCATION,
				payload: nextCardSearchObject,
			});
		}
	};

	render() {
		return (
			<Provider value={{ eventProvider: { events: this.events, setComponentEvents: this.setComponentEvents } }}>
				{this.props.children}
			</Provider>
		);
	}
}

export default withRouter(EventProvider);
